import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { OutreachesPage } from '@apps/web/components/outreaches/outreaches_page';
import { TrainingModePageTourScope } from '@apps/web/components/page_tour/training_mode_page_tour';
import { OUTREACHES_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';
import {
    LEGACY_SURVEYS_TAB,
    OUTREACH_TAB_SEARCH_VALUES,
} from '@apps/web/lib/outreach_tabs';

export const outreachesSearchSchema = z.object({
    tab: z.enum(OUTREACH_TAB_SEARCH_VALUES).optional(),
    // Opens the reschedule panel on the Scheduled tab.
    editId: z.string().uuid().optional(),
    openCreateOutreach: z
        .union([
            z.literal('1'),
            z.literal('true'),
            z.literal(1),
            z.literal(true),
        ])
        .optional(),
    shiftIds: z.string().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
    source: z.string().optional(),
    provider: z.string().optional(),
    externalShiftId: z.coerce.string().optional(),
    sourceUrl: z.string().optional(),
});

const resolveAgentChatDestination = createAgentChatDestinationResolver();

export function handleOutreachesBeforeLoad({
    search,
}: {
    readonly search: z.infer<typeof outreachesSearchSchema>;
}): Promise<void> | void {
    if (search.tab === LEGACY_SURVEYS_TAB) {
        throw redirect({
            to: '/surveys',
            search: {
                organizationId: search.organizationId,
                mode: search.mode,
            },
            replace: true,
        });
    }
    return handleOutreachChatBeforeLoad(search);
}

async function handleOutreachChatBeforeLoad(
    search: z.infer<typeof outreachesSearchSchema>,
): Promise<void> {
    if (
        search.chatId === undefined ||
        search.organizationId === undefined ||
        search.mode === undefined ||
        search.slackWebClaim !== undefined
    ) {
        return;
    }

    const resolution = await resolveAgentChatDestination({
        chatId: search.chatId,
        organizationId: search.organizationId,
        mode: search.mode,
    });
    if (
        resolution.kind === 'stale' ||
        resolution.destination.kind === 'panel'
    ) {
        return;
    }

    const normalizedSearch = normalizeAgentChatSearch(search);
    if (
        normalizedSearch.organizationId === undefined ||
        normalizedSearch.mode === undefined
    ) {
        return;
    }
    throw redirect({
        to: '/agent/runs/$runId',
        params: { runId: resolution.destination.runId },
        search: {
            organizationId: normalizedSearch.organizationId,
            mode: normalizedSearch.mode,
        },
        replace: true,
    });
}

export const Route = createFileRoute('/_app/(main)/outreaches/')({
    head: () => ({
        meta: [
            {
                title: 'Outreach | Phoebe',
            },
        ],
    }),
    validateSearch: outreachesSearchSchema,
    beforeLoad: handleOutreachesBeforeLoad,
    component: OutreachesRoutePage,
});

function OutreachesRoutePage() {
    const { organizationId: deepLinkOrganizationId, mode: deepLinkMode } =
        Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return (
        <TrainingModePageTourScope
            pageId="outreaches"
            organizationId={organizationId}
            ready={organizationId !== null}
            steps={OUTREACHES_PAGE_TOUR_STEPS}
        >
            <OutreachesPage />
        </TrainingModePageTourScope>
    );
}
